import {
  Skeleton,
  SkeletonItem,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import {
  ChevronDownIcon,
  PinIcon,
  PlusIcon,
} from "apps/project-iq/components/icons/ProjectIqIcons";
import { usePersistedFlag } from "apps/project-iq/hooks/usePersistedFlag";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import React from "react";
import { useStyles } from "./SidebarWorkspaceList.styles";
import {
  ISidebarSectionHeaderProps,
  ISidebarWorkspaceListProps,
} from "./SidebarWorkspaceList.types";

const LS_PINNED_COLLAPSED = "piq_sidebar_pinned_collapsed";
const LS_RECENT_COLLAPSED = "piq_sidebar_recent_collapsed";

const SIDEBAR_SKELETON_DARK_TOKENS = {
  "--colorNeutralBackground3": "rgba(255,255,255,0.07)",
  "--colorNeutralBackground1": "rgba(255,255,255,0.16)",
} as React.CSSProperties;

const SKELETON_ROW_COUNT = 3;
const SKELETON_ROW_KEYS = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, i) => `sidebar-workspace-skeleton-row-${i}`,
);

interface ISidebarWorkspaceSkeletonProps {
  isVisuallyExpanded: boolean;
}

const SidebarWorkspaceSkeleton = ({
  isVisuallyExpanded,
}: ISidebarWorkspaceSkeletonProps): JSX.Element => {
  const styles = useStyles();
  return (
    <>
      {SKELETON_ROW_KEYS.map((key) => (
        <div
          key={key}
          className={mergeClasses(
            styles.skeletonRow,
            !isVisuallyExpanded && styles.skeletonRowCollapsed,
          )}
          style={SIDEBAR_SKELETON_DARK_TOKENS}
        >
          <Skeleton
            className={!isVisuallyExpanded ? undefined : styles.skeletonWrapper}
          >
            <SkeletonItem
              animation="wave"
              className={
                isVisuallyExpanded ? styles.skeletonName : styles.skeletonAvatar
              }
            />
          </Skeleton>
        </div>
      ))}
    </>
  );
};

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const SidebarSectionHeader = ({
  label,
  contentId,
  isCollapsed,
  onToggle,
  count,
  action,
}: ISidebarSectionHeaderProps): JSX.Element => {
  const styles = useStyles();

  return (
    <div className={styles.sectionHeader}>
      <button
        type="button"
        className={styles.sectionToggle}
        onClick={onToggle}
        aria-expanded={!isCollapsed}
        aria-controls={contentId}
      >
        <span
          className={mergeClasses(
            styles.sectionChevron,
            isCollapsed && styles.sectionChevronCollapsed,
          )}
        >
          <ChevronDownIcon />
        </span>
        {label}
        {count !== undefined && (
          <span className={styles.countBadge}>{count}</span>
        )}
      </button>
      {action}
    </div>
  );
};

interface ISidebarWorkspaceRowProps {
  workspace: IProjectIQWorkspace;
  isPinned: boolean;
  isSelected: boolean;
  isHovered: boolean;
  isVisuallyExpanded: boolean;
  onSelectWorkspace: (id: string) => void;
  onTogglePin: (id: string, e: React.MouseEvent) => void;
  onHoverWorkspace: (id: string | null) => void;
}

const SidebarWorkspaceRow = ({
  workspace,
  isPinned,
  isSelected,
  isHovered,
  isVisuallyExpanded,
  onSelectWorkspace,
  onTogglePin,
  onHoverWorkspace,
}: ISidebarWorkspaceRowProps): JSX.Element => {
  const styles = useStyles();

  const rowElement = (
    <div
      role="button"
      tabIndex={0}
      className={mergeClasses(
        styles.workspaceItem,
        !isVisuallyExpanded && styles.workspaceItemCollapsed,
        isSelected && styles.workspaceItemSelected,
      )}
      onClick={() => onSelectWorkspace(workspace.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onSelectWorkspace(workspace.id);
        }
      }}
      onMouseEnter={() => onHoverWorkspace(workspace.id)}
      onMouseLeave={() => onHoverWorkspace(null)}
      aria-label={`Select workspace ${workspace.name}`}
      aria-current={isSelected ? "page" : undefined}
    >
      {!isVisuallyExpanded ? (
        <div
          className={mergeClasses(
            styles.workspaceInitialsAvatar,
            isSelected && styles.workspaceInitialsAvatarSelected,
          )}
        >
          {getInitials(workspace.name)}
          {isPinned && (
            <span className={styles.collapsedPinIndicator} aria-hidden="true">
              <PinIcon filled size={11} />
            </span>
          )}
        </div>
      ) : (
        <>
          <span
            className={mergeClasses(
              styles.workspaceDot,
              isPinned ? styles.workspaceDotPinned : styles.workspaceDotRecent,
            )}
            aria-hidden="true"
          />
          <span
            className={mergeClasses(
              styles.workspaceItemName,
              isSelected && styles.workspaceItemNameSelected,
            )}
          >
            {workspace.name}
          </span>
          <button
            className={mergeClasses(
              styles.pinBtn,
              isPinned ? styles.pinBtnPinned : styles.pinBtnUnpinned,
              (isHovered || isPinned) && styles.pinBtnVisible,
            )}
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(workspace.id, e);
            }}
            title={isPinned ? "Unpin workspace" : "Pin to top"}
            aria-label={isPinned ? "Unpin workspace" : "Pin to top"}
          >
            <PinIcon filled={isPinned} size={14} />
          </button>
        </>
      )}
    </div>
  );

  if (isVisuallyExpanded) {
    return workspace.clientName ? (
      <Tooltip
        content={workspace.clientName}
        relationship="description"
        positioning="below"
      >
        {rowElement}
      </Tooltip>
    ) : (
      rowElement
    );
  }

  return (
    <Tooltip content={workspace.name} relationship="label" positioning="after">
      {rowElement}
    </Tooltip>
  );
};

export const SidebarWorkspaceList = ({
  pinnedWorkspaces,
  recentWorkspaces,
  totalWorkspaceCount,
  selectedWorkspaceId,
  hoveredWorkspaceId,
  isVisuallyExpanded,
  isWorkspacesLoading,
  onSelectWorkspace,
  onTogglePin,
  onHoverWorkspace,
  onAddWorkspace,
  onViewAllWorkspaces,
}: ISidebarWorkspaceListProps): JSX.Element => {
  const styles = useStyles();
  const [isPinnedCollapsed, togglePinnedCollapsed] = usePersistedFlag(
    LS_PINNED_COLLAPSED,
    false,
  );
  const [isRecentCollapsed, toggleRecentCollapsed] = usePersistedFlag(
    LS_RECENT_COLLAPSED,
    false,
  );

  const renderRow = (workspace: IProjectIQWorkspace, isPinned: boolean) => (
    <SidebarWorkspaceRow
      key={workspace.id}
      workspace={workspace}
      isPinned={isPinned}
      isSelected={selectedWorkspaceId === workspace.id}
      isHovered={hoveredWorkspaceId === workspace.id}
      isVisuallyExpanded={isVisuallyExpanded}
      onSelectWorkspace={onSelectWorkspace}
      onTogglePin={onTogglePin}
      onHoverWorkspace={onHoverWorkspace}
    />
  );

  if (!isVisuallyExpanded) {
    return (
      <div className={styles.workspaceList}>
        {isWorkspacesLoading && (
          <SidebarWorkspaceSkeleton isVisuallyExpanded={false} />
        )}
        {pinnedWorkspaces.map((workspace) => renderRow(workspace, true))}
        {recentWorkspaces.map((workspace) => renderRow(workspace, false))}
      </div>
    );
  }

  const addAction = (
    <button
      className={styles.addButton}
      onClick={onAddWorkspace}
      aria-label="Create a new workspace"
    >
      <PlusIcon size={11} />
    </button>
  );

  return (
    <>
      <SidebarSectionHeader
        label="Pinned"
        contentId="piq-sidebar-pinned"
        isCollapsed={isPinnedCollapsed}
        onToggle={togglePinnedCollapsed}
        action={addAction}
      />
      {!isPinnedCollapsed && (
        <div id="piq-sidebar-pinned" className={styles.workspaceList}>
          {isWorkspacesLoading && (
            <SidebarWorkspaceSkeleton isVisuallyExpanded />
          )}
          {!isWorkspacesLoading && totalWorkspaceCount === 0 && (
            <span className={styles.workspaceListEmpty}>No workspaces yet</span>
          )}
          {!isWorkspacesLoading &&
            totalWorkspaceCount > 0 &&
            pinnedWorkspaces.length === 0 && (
              <span className={styles.sectionEmpty}>No pinned workspaces</span>
            )}
          {pinnedWorkspaces.map((workspace) => renderRow(workspace, true))}
        </div>
      )}

      <SidebarSectionHeader
        label="Recent"
        contentId="piq-sidebar-recent"
        isCollapsed={isRecentCollapsed}
        onToggle={toggleRecentCollapsed}
        count={isWorkspacesLoading ? undefined : recentWorkspaces.length}
      />
      {!isRecentCollapsed && (
        <div id="piq-sidebar-recent" className={styles.workspaceList}>
          {isWorkspacesLoading && (
            <SidebarWorkspaceSkeleton isVisuallyExpanded />
          )}
          {!isWorkspacesLoading &&
            totalWorkspaceCount > 0 &&
            recentWorkspaces.length === 0 && (
              <span className={styles.sectionEmpty}>No recent workspaces</span>
            )}
          {recentWorkspaces.map((workspace) => renderRow(workspace, false))}
        </div>
      )}

      {totalWorkspaceCount > 0 && (
        <div className={styles.workspaceList}>
          <button className={styles.viewAllLink} onClick={onViewAllWorkspaces}>
            View all workspaces ({totalWorkspaceCount}) →
          </button>
        </div>
      )}
    </>
  );
};
